/** The top-bar "🚆 Create train ▾" dropdown. Two ways to start a train:
 *  from the current selection (needs 2+ picked) or an empty one to build from
 *  scratch. */
import React from "react";
import { useClickOutside } from "../../../../hooks/useClickOutside";
import "./CreateTrainMenu.css";

interface Props {
  selCount: number;
  /** Capture the current selection into a train (enabled at 2+ selected). */
  onFromSelection: () => void;
  /** Start an empty train to build from scratch. */
  onNewEmpty: () => void;
}

const CreateTrainMenu: React.FC<Props> = ({ selCount, onFromSelection, onNewEmpty }) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const canFromSel = selCount >= 2;
  useClickOutside(rootRef, () => setOpen(false), open);

  const pick = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="create-train-menu" ref={rootRef}>
      <button
        type="button"
        className="create-train-menu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span aria-hidden="true">🚆</span>
        Create train
        <span className="create-train-menu-caret" aria-hidden="true">
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <div className="create-train-menu-pop" role="menu">
          <button
            type="button"
            className="create-train-menu-item"
            role="menuitem"
            disabled={!canFromSel}
            onClick={() => pick(onFromSelection)}
          >
            <span className="create-train-menu-item-label">From selected activities</span>
            <span className="create-train-menu-item-sub">
              {canFromSel
                ? `Capture the ${selCount} selected into a train`
                : "Select 2+ activities first"}
            </span>
          </button>
          <button
            type="button"
            className="create-train-menu-item"
            role="menuitem"
            onClick={() => pick(onNewEmpty)}
          >
            <span className="create-train-menu-item-label">New empty train</span>
            <span className="create-train-menu-item-sub">Build a sequence from scratch</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default CreateTrainMenu;
